import { Box } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { shuffle } from "lodash";
import { CategorySection } from "./components/section-category";
import LoaderPage from "components/layout/LoaderPage";
import useCategoryData from "./hooks/use-category-data.hook";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RelevantSection from "./components/relevant-section";
import useNewsData from "./hooks/use-news-data.hook";
import { useSourceSearch } from "features/SourceSearch";
import useBoclipsSearch from "./hooks/use-boclips-search.hook";
import BoclipsPlayer from "./components/boclips-player";
import { biasRating } from "data/biasRatingLabel";
import { boClipsLabel } from "features/SourceSearch/data/search-fields.data";
import { showBoclipsSearch, showDatabaseSearch } from "config";
import { useSearchCredentials } from "features/Group/hooks";
import { getFilterDateValueKeys } from "utils/date-filter.util";
import useMainEbsco from "./hooks/use-main-ebsco.hook";

export default function News() {
  const [loading, setLoading] = useState(true);
  const { currentSearchTerm, currentTimeframe, startDate, endDate } = useSourceSearch();
  const { credentials } = useSearchCredentials();
  const [boclipsSearch, setBoClipsSearch] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const { source, search } = useParams();
  const [tourNext, setTourNext] = useState(null);

  const randomizedBiasRating = useMemo(() => {
    return shuffle([...biasRating]);
  }, [currentSearchTerm]);

  const { fetchNewsCategoryData, categoriesCopy } = useCategoryData(() => { });
  const { combinedSearch, fetchMore } = useNewsData(
    setLoading,
    search,
    categoriesCopy,
    setLoadingStates
  );
  const { getBoclipsSearchData } = useBoclipsSearch(
    setBoClipsSearch,
    currentSearchTerm,
    setLoadingStates
  );
  const { categories, boclipsPopup } = useSelector((state) => state.data);
  const { isHomeschool } = useSelector((state) => state.account);

  const randomizedDatabaseCategories = useMemo(() => {
    return shuffle((Array.isArray(categories) ? categories : []).filter((category) => !category.disabled));
  }, [currentSearchTerm, categories]);

  function checkedSource(currentSource) {
    return currentSource == null ? "categories" : currentSource;
  }

  const activeSource = checkedSource(source);
  const showMainEbsco = activeSource === "categories" && showDatabaseSearch && credentials.ebsco?.isEnabled;
  const mainEbsco = useMainEbsco(showMainEbsco, search, getFilterDateValueKeys(currentTimeframe, startDate, endDate));

  useEffect(() => {
    fetchNewsCategoryData();
  }, [source]);

  useEffect(() => {
    if (
      (showBoclipsSearch || isHomeschool) &&
      currentSearchTerm &&
      activeSource !== "partner-publishers" &&
      activeSource !== "databases"
    ) {
      getBoclipsSearchData(setBoClipsSearch, currentSearchTerm);
    }
  }, [currentSearchTerm, activeSource, isHomeschool]);

  useEffect(() => {
    function triggerAfterDataLoad({ detail }) {
      setTourNext(detail);
    }
    window.addEventListener("tour:app:search:results", triggerAfterDataLoad);
    return () =>
      window.removeEventListener(
        "tour:app:search:results",
        triggerAfterDataLoad
      );
  }, []);

  useEffect(() => {
    if (!loading && tourNext != null) {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(tourNext.action, { detail: tourNext.index })
        );
        setTourNext(null);
      }, 1000);
    }
  }, [loading, tourNext]);

  if (!categories.length) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoaderPage />
      </Box>
    );
  }

  const augmentedCategories = [
    ...categories.filter((category) => !category.isHidden_ysn).slice(0, 1),
    ...(showMainEbsco ? [{ category_id: "main-ebsco", categoryName_chr: "EBSCO" }] : []),
    ...((showBoclipsSearch || isHomeschool) &&
      activeSource !== "partner-publishers" &&
      activeSource !== "databases"
      ? [{ category_id: "boclips", categoryName_chr: boClipsLabel }]
      : []),
    ...categories.filter((category) => !category.isHidden_ysn).slice(1),
  ];

  return (
    <React.Fragment>
      <RelevantSection />
      {(activeSource === "rating"
        ? randomizedBiasRating
        : activeSource === "databases"
          ? randomizedDatabaseCategories
          : augmentedCategories
      ).map(
        (category, index) => {
          const categoryName = category.categoryName_chr || category.label;
          const categoryId = category.category_id || category.key;
          const sectionNews =
            combinedSearch.find(
              (search) => search.media === categoryName
            ) || null;

          return (
            <Box
              key={categoryId}
              id={index === 0 ? "tour-first-cat-section" : undefined}
            >
              <CategorySection
                category={category}
                sectionNews={
                  categoryId === "main-ebsco"
                    ? mainEbsco.section
                    : categoryId === "boclips"
                    ? boclipsSearch
                    : sectionNews
                }
                source={categoryId === "main-ebsco" ? "databases" : activeSource}
                isLoading={categoryId === "main-ebsco" ? mainEbsco.loading : loadingStates[categoryName] || loading}
                onFetchMore={categoryId === "main-ebsco" ? mainEbsco.fetchMore : fetchMore}
              />
            </Box>
          );
        }
      )}
      {boclipsPopup && <BoclipsPlayer />}
    </React.Fragment>
  );
}
